import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import type { PropsWithChildren } from "react";
import { generatePath, NavLink } from "react-router-dom";

import {
	CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH,
	CLIENT_CONTACT_PATH,
	PROJECT_DETAILS_PATH_WITH_TAB_ID,
	QUOTA_DETAILS_PATH,
} from "~/constants/appRoutes.ts";
import {
	FIXED_PRICE_OPTIONS,
	INVOICE_BY_MAIL_OPTIONS,
	MONTHLY_BILLING_OPTIONS,
	PROJECT_BILLING_SETTINGS_INVOICE_TYPE_OPTIONS,
	SKILL_BASED_OPTIONS,
	TRAVEL_EXPENSES_INCLUDED_OPTIONS,
} from "~/constants/form.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import { billingSettingsQueries } from "~/modules/billing/api/billingSettings/billingSettingsQueries.ts";
import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { useBillingSettingsLookups } from "~/modules/billing/hooks/useBillingSettingsLookups.ts";
import {
	computeBillingSettingsDifferences,
	resolveBillingSettingsDisplay,
} from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import type { Quota } from "~/modules/client/api/quota/quotaTypes.ts";
import { ProjectDetailsTabName } from "~/modules/project/types/projectViewTypes.ts";
import { resolveOptionLabel } from "~/utils/form/formUtils.ts";
import { nl2br } from "~/utils/stringUtils.tsx";

type Props = {
	clientId: string;
	orderBillingSettings: BillingSettings | null;
	projectId: string;
	quota?: Quota;
};

const Entry = ({ children, highlighted = false, label }: {
	label: string,
	highlighted?: boolean
} & PropsWithChildren) => {
	return <div className="flex items-center gap-x-4 py-1">
		<dt className="w-44 text-sm font-medium leading-6 text-gray-900">{label}</dt>
		<dd className={clsx("text-sm leading-6 text-gray-700", highlighted && "font-bold")}>
			{children}
		</dd>
	</div>;
};

const OrderBillingSettingsDetails = ({ clientId, orderBillingSettings, projectId, quota }: Props) => {
	const { data: projectBillingSettingsData } = useQuery(billingSettingsQueries.byProject(projectId));
	const lookups = useBillingSettingsLookups(clientId);


	if (!projectBillingSettingsData) {
		return null;
	}

	const resolvedBillingSettings = orderBillingSettings ?? projectBillingSettingsData;
	const display = resolveBillingSettingsDisplay(resolvedBillingSettings, lookups);
	const diffs = orderBillingSettings
		? computeBillingSettingsDifferences(orderBillingSettings, projectBillingSettingsData, lookups, clientId)
		: null;

	if (quota) {
		return <dl className="m-4 divide-y divide-gray-100 rounded-lg bg-white px-4 py-2">
			<Entry label="Abrechnungskontingent">
				<NavLink className="flex items-center gap-x-1 hover:text-secondary-500"
						 to={generatePath(QUOTA_DETAILS_PATH, { clientId, quotaId: quota.id })}>
					Abrechnungskontingent &quot;{quota.title}&quot;
					<ArrowTopRightOnSquareIcon className="ml-1 size-4" />
				</NavLink>
			</Entry>
		</dl>;
	}

	let invoiceRecipientDisplayValue: React.ReactNode = EMPTY_VALUE_PLACEHOLDER;
	if (display.invoiceRecipient) {
		invoiceRecipientDisplayValue = <NavLink className="flex items-center gap-x-1 hover:text-secondary-500"
												to={generatePath(CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH, {
													clientId,
													centralInvoiceRecipientId: display.invoiceRecipient.id,
												})}>
			{display.invoiceRecipient.displayName}
			<ArrowTopRightOnSquareIcon className="ml-1 size-4" />
		</NavLink>;
	} else if (display.clientContactPerson) {
		invoiceRecipientDisplayValue = <NavLink className="flex items-center gap-x-1 hover:text-secondary-500"
												to={generatePath(CLIENT_CONTACT_PATH, { contactId: display.clientContactPerson.id })}>
			{display.clientContactPerson.fullName}
			<ArrowTopRightOnSquareIcon className="ml-1 size-4" />
		</NavLink>;
	}

	const hasDifferences = diffs && Object.values(diffs).some(diff => diff.isDifferent);

	return <div className="m-4 grid grid-cols-2 rounded-lg bg-white px-4 py-2">
		<div className="divide-y divide-gray-100">
			<Entry label="Rechnungstyp"
				   highlighted={diffs?.invoiceTypeId.isDifferent}>
				{resolveOptionLabel(
					resolvedBillingSettings.invoiceTypeId ?? invoiceTypeId.RegularInvoice,
					PROJECT_BILLING_SETTINGS_INVOICE_TYPE_OPTIONS,
				)}
			</Entry>
			<Entry label="Abrechnungsart"
				   highlighted={diffs?.isFixedPrice.isDifferent}>
				{resolveOptionLabel(
					resolvedBillingSettings.isFixedPrice ?? false,
					FIXED_PRICE_OPTIONS,
				)}
			</Entry>
			<Entry label="Abrechnungskategorie "
				   highlighted={diffs?.isSkillBased.isDifferent}>
				{resolveOptionLabel(
					resolvedBillingSettings.isSkillBased ?? false,
					SKILL_BASED_OPTIONS,
				)}
			</Entry>
			<Entry label="Reisekosten"
				   highlighted={diffs?.travelExpensesIncluded.isDifferent}>
				{resolveOptionLabel(
					resolvedBillingSettings.travelExpensesIncluded ?? false,
					TRAVEL_EXPENSES_INCLUDED_OPTIONS,
				)}
			</Entry>
			<Entry label="Abrechnungsintervall"
				   highlighted={diffs?.isBilledMonthly.isDifferent}>
				{resolveOptionLabel(
					resolvedBillingSettings.isBilledMonthly ?? false,
					MONTHLY_BILLING_OPTIONS,
				)}
			</Entry>
			<Entry label="Zahlungsziel"
				   highlighted={diffs?.paymentTargetDays.isDifferent}>
				{`${resolvedBillingSettings.paymentTargetDays ?? 30} Tage`}
			</Entry>
			<Entry label="Währung"
				   highlighted={diffs?.currencyDisplayName.isDifferent}>
				{display.currencyDisplayName || EMPTY_VALUE_PLACEHOLDER}
			</Entry>
		</div>
		<div className="divide-y divide-gray-100">
			<Entry label="Rechnungsempfänger"
				   highlighted={diffs?.resolvedInvoiceRecipient.isDifferent}>
				{invoiceRecipientDisplayValue}
			</Entry>
			<Entry label="Versand per"
				   highlighted={diffs?.invoiceByMail.isDifferent}>
				{resolveOptionLabel(
					resolvedBillingSettings.invoiceByMail ?? false,
					INVOICE_BY_MAIL_OPTIONS)}
			</Entry>
			<Entry label="Zus. E-Mail-Empfänger"
				   highlighted={diffs?.additionalEmailRecipients.isDifferent}>
				{display.emailRecipients.length > 0 ? display.emailRecipients.join(", ") : EMPTY_VALUE_PLACEHOLDER}
			</Entry>
			<Entry label="Abrechnungshinweise"
				   highlighted={diffs?.billingInstructions.isDifferent}>
				{resolvedBillingSettings.billingInstructions ? nl2br(resolvedBillingSettings.billingInstructions) : EMPTY_VALUE_PLACEHOLDER}
			</Entry>
		</div>
		{hasDifferences && <div className="col-span-2 mt-4 flex items-center text-right text-sm font-light">
			<span className="mr-1.5 font-bold">bold</span> = Einstellung weicht
			von&nbsp;<NavLink className="font-normal text-primary-500 hover:underline"
							  to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
								  tabId: ProjectDetailsTabName.BillingSettings,
								  projectId,
							  })}>globalen
			Projekteinstellungen</NavLink>&nbsp;ab.
		</div>}
		{!hasDifferences && <div className="col-span-2 mt-4 flex items-center text-right text-sm font-light">
			Alle Einstellungen entsprechen den&nbsp;<NavLink className="font-normal text-primary-500 hover:underline"
															 to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
																 tabId: ProjectDetailsTabName.BillingSettings,
																 projectId,
															 })}>globalen
			Projekteinstellungen</NavLink>.
		</div>
		}
	</div>;
};

export default OrderBillingSettingsDetails;